import React, { useEffect, useState, useCallback } from 'react';
import { useHttp, Input, Select, Table, DropdownMenu, Spinner } from '@veda/dashboard-ui';
import { MdBusiness, MdPersonAdd, MdEdit, MdPersonRemove, MdGroup, MdClose } from 'react-icons/md';

function Btn({ onClick, children, className = '', disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}
function Modale({ aperto, titolo, onChiudi, children }) {
  if (!aperto) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onChiudi(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mt-16">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-800 dark:text-white">{titolo}</h2>
          <button onClick={onChiudi} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <MdClose size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const RUOLO_LABEL = { admin: 'Amministratore', membro: 'Membro' };
const FORMA_GIURIDICA_OPTIONS = [
  { value: '',                  label: 'Non specificata' },
  { value: 'S.r.l.',            label: 'S.r.l.' },
  { value: 'S.p.A.',            label: 'S.p.A.' },
  { value: 'S.n.c.',            label: 'S.n.c.' },
  { value: 'S.a.s.',            label: 'S.a.s.' },
  { value: 'Consorzio',         label: 'Consorzio' },
  { value: 'Cooperativa',       label: 'Cooperativa' },
  { value: 'Impresa individuale', label: 'Impresa individuale' },
];

// ─── Form crea / modifica profilo aziendale ─────────────────────────────────────

function FormCompany({ iniziale, onSalva, loading }) {
  const [form, setForm] = useState({
    ragione_sociale:       iniziale?.ragioneSociale      ?? '',
    codice_fiscale:        iniziale?.codiceFiscale       ?? '',
    partita_iva:           iniziale?.partitaIva          ?? '',
    rappresentante_legale: iniziale?.rappresentanteLegale ?? '',
    pec:                   iniziale?.pec                 ?? '',
    email:                 iniziale?.email               ?? '',
    telefono:              iniziale?.telefono            ?? '',
    forma_giuridica:       iniziale?.formaGiuridica      ?? '',
    indirizzo:             iniziale?.indirizzo           ?? '',
    cap:                   iniziale?.cap                 ?? '',
    comune:                iniziale?.comune              ?? '',
    provincia:             iniziale?.provincia           ?? '',
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e?.target ? e.target.value : e }));

  return (
    <div className="space-y-4">
      <Input label="Ragione sociale *" value={form.ragione_sociale} onChange={set('ragione_sociale')} />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Codice fiscale" value={form.codice_fiscale} onChange={set('codice_fiscale')} />
        <Input label="Partita IVA"    value={form.partita_iva}    onChange={set('partita_iva')} />
      </div>
      <Select
        label="Forma giuridica"
        value={form.forma_giuridica}
        onChange={set('forma_giuridica')}
        options={FORMA_GIURIDICA_OPTIONS}
      />
      <Input label="Rappresentante legale" value={form.rappresentante_legale} onChange={set('rappresentante_legale')} />
      <div className="grid grid-cols-2 gap-4">
        <Input label="PEC"   value={form.pec}   onChange={set('pec')}   type="email" />
        <Input label="Email" value={form.email} onChange={set('email')} type="email" />
      </div>
      <Input label="Telefono" value={form.telefono} onChange={set('telefono')} />
      <Input label="Indirizzo" value={form.indirizzo} onChange={set('indirizzo')} />
      <div className="grid grid-cols-3 gap-4">
        <Input label="CAP"    value={form.cap}      onChange={set('cap')} />
        <Input label="Comune" value={form.comune}   onChange={set('comune')} />
        <Input label="Provincia (2 lett.)" value={form.provincia} onChange={set('provincia')} maxLength={2} />
      </div>
      <div className="flex justify-end pt-2">
        <button
          onClick={() => onSalva(form)}
          disabled={loading || !form.ragione_sociale}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {loading ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>
    </div>
  );
}

// ─── Form invita membro ───────────────────────────────────────────────────────

function FormInvita({ companyId, onSuccess }) {
  const { request } = useHttp();
  const [email,   setEmail]   = useState('');
  const [ruolo,   setRuolo]   = useState('membro');
  const [loading, setLoading] = useState(false);
  const [errore,  setErrore]  = useState('');

  const invia = () => {
    if (!email) return;
    setLoading(true);
    setErrore('');
    request(`/api/v1/operatore/company/${companyId}/invita`, {
      method: 'post',
      body: JSON.stringify({ email, ruolo }),
      onSuccess: () => { setLoading(false); setEmail(''); onSuccess?.(); },
      onFormError: (err) => { setLoading(false); setErrore(err?.errors?.email?.[0] ?? err?.message ?? 'Errore.'); },
    });
  };

  return (
    <div className="space-y-4">
      <Input label="Email collaboratore *" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
      <Select
        label="Ruolo"
        value={ruolo}
        onChange={(e) => setRuolo(e?.target ? e.target.value : e)}
        options={[
          { value: 'membro', label: 'Membro' },
          { value: 'admin',  label: 'Amministratore' },
        ]}
      />
      {errore && <p className="text-sm text-red-600">{errore}</p>}
      <div className="flex justify-end">
        <button
          onClick={invia}
          disabled={loading || !email}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {loading ? 'Invio...' : 'Invia invito'}
        </button>
      </div>
    </div>
  );
}

// ─── Badge ruolo ─────────────────────────────────────────────────────────────

function BadgeRuolo({ ruolo }) {
  const cls = ruolo === 'admin'
    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${cls}`}>
      {RUOLO_LABEL[ruolo] ?? ruolo}
    </span>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function CompanyPage() {
  const { request } = useHttp();

  const [org,     setOrg]     = useState(undefined); // undefined = caricamento, null = nessuna
  const [membri,  setMembri]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [myId,    setMyId]    = useState(null);

  const [modaleCrea,     setModaleCrea]     = useState(false);
  const [modaleModifica, setModaleModifica] = useState(false);
  const [modaleInvita,   setModaleInvita]   = useState(false);
  const [salvaLoading,   setSalvaLoading]   = useState(false);
  const [msg,            setMsg]            = useState('');

  // Feedback transitorio per esiti/errori delle azioni (incluso il 403 di un
  // tentativo non autorizzato, che il backend ora restituisce esplicitamente).
  const mostraMsg = (testo) => { setMsg(testo); setTimeout(() => setMsg(''), 4000); };

  // Carica ID operatore corrente
  useEffect(() => {
    request('/api/v1/operatore/me', {
      method: 'get',
      onSuccess: (data) => setMyId(data?.operatore?.id ?? null),
      onFormError: () => {},
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caricaOrg = useCallback(() => {
    setLoading(true);
    request('/api/v1/operatore/company', {
      method: 'get',
      onSuccess: (data) => {
        // Backend risponde { company: null } se non esiste, oppure la view diretta
        if (!data || data.company === null || !data.id) {
          setOrg(null);
          setLoading(false);
          return;
        }
        setOrg(data);
        request(`/api/v1/operatore/company/${data.id}/membri`, {
          method: 'get',
          onSuccess: (list) => { setMembri(Array.isArray(list) ? list : []); setLoading(false); },
          onFormError: () => setLoading(false),
        });
      },
      onFormError: () => { setOrg(null); setLoading(false); },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { caricaOrg(); }, [caricaOrg]);

  const creaOrg = (form) => {
    setSalvaLoading(true);
    request('/api/v1/operatore/company', {
      method: 'post',
      body: JSON.stringify(form),
      onSuccess: () => { setSalvaLoading(false); caricaOrg(); },
      onFormError: (err) => { setSalvaLoading(false); mostraMsg(err?.message ?? 'Impossibile creare l\'azienda.'); },
    });
  };

  const modificaOrg = (form) => {
    setSalvaLoading(true);
    request(`/api/v1/operatore/company/${org.id}`, {
      method: 'put',
      body: JSON.stringify(form),
      onSuccess: () => { setSalvaLoading(false); setModaleModifica(false); caricaOrg(); },
      onFormError: (err) => { setSalvaLoading(false); mostraMsg(err?.message ?? 'Impossibile aggiornare l\'azienda.'); },
    });
  };

  const rimuoviMembro = (membroId) => {
    request(`/api/v1/operatore/company/${org.id}/membri/${membroId}`, {
      method: 'delete',
      onSuccess: () => caricaOrg(),
      onFormError: (err) => mostraMsg(err?.message ?? 'Impossibile rimuovere il membro.'),
    });
  };

  const revocaInvito = (invitoId) => {
    request(`/api/v1/operatore/company/${org.id}/inviti/${invitoId}`, {
      method: 'delete',
      onSuccess: () => caricaOrg(),
      onFormError: (err) => mostraMsg(err?.message ?? 'Impossibile revocare l\'invito.'),
    });
  };

  const myRuolo = membri.find((m) => m.operatoreId === myId)?.ruolo;
  const isAdmin = myRuolo === 'admin';

  // Banner di feedback (esiti/errori azioni, incluso 403 non autorizzato).
  const bannerMsg = msg ? (
    <div className="fixed top-4 right-4 z-50 max-w-sm px-4 py-3 rounded-lg bg-gray-900 text-white text-sm shadow-lg">
      {msg}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        {bannerMsg}
        <Spinner />
      </div>
    );
  }

  // ── Nessun profilo aziendale ──
  if (org === null) {
    // Form fullscreen — occupa tutta la larghezza della pagina
    if (modaleCrea) {
      return (
        <div className="p-6 max-w-5xl mx-auto">
          {bannerMsg}

          {/* Header navigazione */}
          <button
            onClick={() => setModaleCrea(false)}
            className="mb-6 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white flex items-center gap-1.5 transition-colors"
          >
            ← Torna indietro
          </button>

          {/* Intestazione */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 flex-shrink-0">
                <MdBusiness size={22} />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Crea il tuo profilo aziendale</h1>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-2xl">
              Registra la tua azienda o consorzio per presentare proposte PPP come persona giuridica,
              come richiesto dall'art. 193 del D.lgs 36/2023. Una volta creata, potrai invitare
              colleghi e collaboratori a lavorare insieme sulle stesse proposte.
            </p>
          </div>

          {/* Nota admin */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800 mb-6">
            <h3 className="font-semibold text-amber-700 dark:text-amber-300 mb-1 text-sm">Sarai automaticamente amministratore</h3>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Chi crea il profilo aziendale diventa l'amministratore e potrà invitare collaboratori,
              gestire i dati aziendali e assegnare ruoli ai membri.
            </p>
          </div>

          {/* Form fullscreen */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              Dati aziendali
            </h2>
            <FormCompany onSalva={creaOrg} loading={salvaLoading} />
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto p-6">
        {bannerMsg}
        <div className="text-center pt-12 pb-8">
          <MdBusiness className="mx-auto text-6xl text-gray-300 dark:text-gray-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 dark:text-white mb-2">
            Benvenuto nel portale operatori
          </h1>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Per presentare proposte PPP devi essere associato a un'azienda. Cosa vuoi fare?
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setModaleCrea(true)}
            className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 dark:border-blue-800 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 transition-all text-center group"
          >
            <div className="w-14 h-14 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
              <MdBusiness size={28} />
            </div>
            <div>
              <p className="font-semibold text-gray-800 dark:text-white">Crea la tua azienda</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Registra la tua azienda e diventa amministratore
              </p>
            </div>
          </button>

          <div className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-center">
            <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300">
              <MdGroup size={28} />
            </div>
            <div>
              <p className="font-semibold text-gray-800 dark:text-white">Hai ricevuto un invito?</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Usa il link ricevuto via email per unirti a un'azienda esistente
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Profilo aziendale trovato ──
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {bannerMsg}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-2xl font-bold flex-shrink-0">
            {(org.ragioneSociale ?? '?')[0].toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">{org.ragioneSociale}</h1>
            {org.formaGiuridica && (
              <span className="text-sm text-gray-500 dark:text-gray-400">{org.formaGiuridica}</span>
            )}
            <div className="flex items-center gap-2 mt-1">
              <MdGroup className="text-gray-400 text-sm" />
              <span className="text-sm text-gray-500">{org.numeroMembri} {org.numeroMembri === 1 ? 'membro' : 'membri'}</span>
              {myRuolo && <BadgeRuolo ruolo={myRuolo} />}
            </div>
          </div>
        </div>
        {isAdmin && (
          <Btn onClick={() => setModaleModifica(true)} className="text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200">
            <MdEdit /> Modifica
          </Btn>
        )}
      </div>

      {/* Dati aziendali */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-4">Dati aziendali</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
          {org.codiceFiscale && (<><dt className="text-sm text-gray-500">Codice fiscale</dt><dd className="text-sm text-gray-800 dark:text-white">{org.codiceFiscale}</dd></>)}
          {org.partitaIva    && (<><dt className="text-sm text-gray-500">Partita IVA</dt>   <dd className="text-sm text-gray-800 dark:text-white">{org.partitaIva}</dd></>)}
          {org.pec           && (<><dt className="text-sm text-gray-500">PEC</dt>           <dd className="text-sm text-gray-800 dark:text-white">{org.pec}</dd></>)}
          {org.email         && (<><dt className="text-sm text-gray-500">Email</dt>         <dd className="text-sm text-gray-800 dark:text-white">{org.email}</dd></>)}
          {org.telefono      && (<><dt className="text-sm text-gray-500">Telefono</dt>      <dd className="text-sm text-gray-800 dark:text-white">{org.telefono}</dd></>)}
          {org.comune        && (
            <>
              <dt className="text-sm text-gray-500">Sede</dt>
              <dd className="text-sm text-gray-800 dark:text-white">
                {[org.indirizzo, org.comune, org.provincia].filter(Boolean).join(', ')}
                {org.cap && ` (${org.cap})`}
              </dd>
            </>
          )}
        </dl>
      </div>

      {/* Lista membri */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200">Membri</h2>
          {isAdmin && (
            <Btn onClick={() => setModaleInvita(true)} className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100">
              <MdPersonAdd /> Invita collaboratore
            </Btn>
          )}
        </div>

        <Table
          cols={[
            { name: 'nome',   label: 'Nome / Email' },
            { name: 'ruolo',  label: 'Ruolo',  size: 'w-36' },
            { name: 'stato',  label: 'Stato',  size: 'w-32' },
            { name: 'azioni', label: '',       size: 'w-10', orderable: 0 },
          ]}
          cells_style={{
            ruolo:  (row) => <BadgeRuolo ruolo={row.ruolo} />,
            stato:  (row) => row.stato === 'invitato'
              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">In attesa</span>
              : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Attivo</span>,
            azioni: (row) => {
              if (row.stato === 'invitato') {
                if (!isAdmin || !row.invitoId) return null;
                return (
                  <DropdownMenu
                    menu_items={[
                      <button key="revoca" onClick={() => revocaInvito(row.invitoId)}
                        className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <MdClose /> Revoca invito
                      </button>,
                    ]}
                  />
                );
              }
              const canRemove = isAdmin && row.operatoreId !== myId;
              const isSelf    = row.operatoreId === myId;
              if (!canRemove && !isSelf) return null;
              return (
                <DropdownMenu
                  menu_items={[
                    canRemove && (
                      <button key="rimuovi" onClick={() => rimuoviMembro(row.operatoreId)}
                        className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <MdPersonRemove /> Rimuovi membro
                      </button>
                    ),
                    isSelf && (
                      <button key="lascia" onClick={() => rimuoviMembro(row.operatoreId)}
                        className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <MdPersonRemove /> Lascia l&apos;azienda
                      </button>
                    ),
                  ].filter(Boolean)}
                />
              );
            },
          }}
          dataset={membri}
        />
      </div>

      <Modale aperto={modaleModifica} titolo="Modifica profilo aziendale" onChiudi={() => setModaleModifica(false)}>
        <FormCompany iniziale={org} onSalva={modificaOrg} loading={salvaLoading} />
      </Modale>

      <Modale aperto={modaleInvita} titolo="Invita collaboratore" onChiudi={() => setModaleInvita(false)}>
        <FormInvita
          companyId={org.id}
          onSuccess={() => { setModaleInvita(false); caricaOrg(); }}
        />
      </Modale>
    </div>
  );
}
